import { useMatch, useRouter } from '@tanstack/react-router';
import { createContext, useContext, useLayoutEffect } from 'react';

/**
 * Lets a page render its own title — plain text, or an edit-in-place field —
 * as a crumb in the app header, so the page name appears once instead of as a
 * crumb plus an h1 repeating it.
 *
 * A claim is anchored to a route: a match id (the default, from the rendering
 * route) or a route id (to title an ancestor, as the workflow editor does for
 * its layout route). The header replaces that route's crumb — or the nearest
 * ancestor's that has one — with a slot element, and the claimant portals its
 * content into the slot.
 */
interface HeaderTitleContextValue {
  /** Claimed anchors, counted so nested claimants of one anchor unwind. */
  claims: Readonly<Record<string, number>>;
  claim: (anchor: string) => () => void;
  slots: Readonly<Record<string, HTMLElement>>;
  setSlot: (anchor: string, element: HTMLElement | null) => void;
}

export const HeaderTitleContext = createContext<HeaderTitleContextValue | null>(
  null,
);

/** For the header: the anchors pages have claimed. */
export function useHeaderTitleClaims(): readonly string[] {
  const claims = useContext(HeaderTitleContext)?.claims;
  return Object.keys(claims ?? {});
}

/**
 * Whether this component can claim a header crumb: it renders inside the app
 * shell and a router. Outside them (tests, stories) the title renders inline.
 * Branch on this before rendering a component that calls `useHeaderTitleSlot`.
 */
export function useCanClaimHeaderTitle(): boolean {
  const context = useContext(HeaderTitleContext);
  const router: unknown = useRouter({ warn: false });
  return context !== null && Boolean(router);
}

/**
 * Claim a header crumb while `enabled`. Returns the element to portal the
 * title into, `null` while the slot mounts, or `undefined` when there is no
 * route to anchor to — render inline then. Call only where
 * `useCanClaimHeaderTitle()` is true: it reads the nearest route match.
 */
export function useHeaderTitleSlot(
  enabled: boolean,
  routeId?: string,
): HTMLElement | null | undefined {
  const context = useContext(HeaderTitleContext);
  const matchId = useMatch({
    strict: false,
    shouldThrow: false,
    select: (match) => match.id,
  });
  const anchor = routeId ?? matchId;
  const claim = context?.claim;

  // Layout effect so the loader crumb is swapped for the title before paint.
  useLayoutEffect(() => {
    if (!enabled || !claim || !anchor) return;
    return claim(anchor);
  }, [enabled, claim, anchor]);

  if (!context || !anchor) return undefined;
  return enabled ? (context.slots[anchor] ?? null) : null;
}
